import { getScrollContainer, scrollTo } from '@theme/scroll-container';

/**
 * Smooth-scrolls in-page anchor links to their target section.
 *
 * A plain `href="#about"` cannot be left to the browser here. At >=990px the
 * theme's squeeze layout makes `.page-wrapper` the scroll container and gives
 * `document.scrollingElement` `overflow: hidden`, so the native jump either
 * does nothing or moves the wrong box. Routing through `@theme/scroll-container`
 * targets whichever element actually scrolls at the current breakpoint.
 *
 * Handles three cases:
 *   - a click on a same-page anchor
 *   - a page loaded with a hash already in the URL (arriving from another page)
 *   - the back/forward buttons moving between hashes
 */

const HEADER_OFFSET_FALLBACK = 0;

/**
 * Height of whatever is pinned to the top of the viewport, so the target does
 * not land underneath it.
 *
 * `--header-group-height` is the theme's own answer, but the inline script in
 * layout/theme.liquid that maintains it looks for `header-component` - the
 * stock header's custom element. sections/custom-header.liquid does not use
 * that element, so on those templates the variable is never set and the
 * variable-only version of this offset silently resolved to zero.
 *
 * Measuring a sticky element directly covers both headers, and falls back to
 * the variable for any pinned element this selector does not know about.
 *
 * @returns {number} Height in pixels.
 */
function getHeaderOffset() {
  const stickyHeader = document.querySelector('.custom-header-section, .header-section, #header-group');

  if (stickyHeader instanceof HTMLElement) {
    const { position } = getComputedStyle(stickyHeader);

    // Only counts when it actually stays on screen. A static header scrolls
    // away with the page, so offsetting for it would overshoot every target.
    if (position === 'sticky' || position === 'fixed') {
      return stickyHeader.getBoundingClientRect().height;
    }
  }

  const raw = getComputedStyle(document.body).getPropertyValue('--header-group-height');
  const parsed = Number.parseFloat(raw);

  return Number.isFinite(parsed) ? parsed : HEADER_OFFSET_FALLBACK;
}

/**
 * Mirrors Liquid's `handleize`, which snippets/section-anchor.liquid runs over
 * the merchant's Anchor ID before writing it into the DOM.
 *
 * Both ends are merchant-typed and they are typed in different places, so they
 * drift: an anchor of "#Portfolio" is emitted as id="portfolio", and a menu URL
 * of "#Portfolio" would then match nothing. Normalising the link the same way
 * makes the two agree however either was capitalised or punctuated.
 *
 * @param {string} value
 * @returns {string}
 */
function handleize(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Resolves a hash to an element, guarding against selectors the DOM cannot
 * parse. A menu URL is merchant-authored, so `#` or `#123` are both reachable.
 *
 * @param {string} hash - The raw hash, including its leading `#`.
 * @returns {HTMLElement | null}
 */
function resolveTarget(hash) {
  if (!hash || hash === '#') return null;

  const raw = decodeURIComponent(hash.slice(1));
  if (!raw) return null;

  // getElementById rather than querySelector: an id starting with a digit is
  // valid HTML but an invalid CSS selector, and would throw.
  // The exact id is tried first so a hand-written anchor elsewhere in the theme
  // still works; the handleized form is the fallback for the Anchor ID setting.
  return document.getElementById(raw) ?? document.getElementById(handleize(raw));
}

/**
 * @param {HTMLElement} target
 * @param {ScrollBehavior} behavior
 */
function scrollToTarget(target, behavior) {
  const container = getScrollContainer();

  // Measured against the container rather than the viewport so the maths holds
  // in both layouts: on desktop the container is itself a scrolled box, so its
  // own top edge is the reference point, not the window's.
  const containerTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
  const offset = target.getBoundingClientRect().top - containerTop;

  scrollTo({
    top: container.scrollTop + offset - getHeaderOffset(),
    behavior,
  });
}

/**
 * Moves focus to the target so keyboard and screen-reader users follow the
 * jump, matching what a native anchor would have done.
 *
 * @param {HTMLElement} target
 */
function focusTarget(target) {
  // A section is not focusable by default. tabindex="-1" makes it programmatically
  // focusable without adding it to the tab order.
  if (!target.hasAttribute('tabindex')) {
    target.setAttribute('tabindex', '-1');
  }

  target.focus({ preventScroll: true });
}

/**
 * Closes the `<theme-drawer>` a link was clicked inside, if any.
 *
 * Drawers are normally dismissed as a side effect of the navigation their links
 * trigger. An in-page anchor is handled without navigating, so the drawer has
 * to be told explicitly - otherwise the page scrolls behind a panel that is
 * still covering it.
 *
 * @param {HTMLAnchorElement} link
 * @returns {Promise<void> | null} Resolves when the drawer has finished closing, or null if there was none.
 */
function closeEnclosingDrawer(link) {
  const drawer = link.closest('theme-drawer');
  if (!drawer) return null;

  // Duck-typed rather than imported: this module must not pull theme-drawer.js
  // onto every page for a method it only needs when a drawer is actually open.
  // `close` is also absent for the frame before the element upgrades.
  if ('close' in drawer && typeof drawer.close === 'function') {
    // Resolves once the close animation has finished. Below the menu breakpoint
    // the panel is a modal dialog, which the browser scroll-locks - scrolling
    // while that is still up does nothing, so the caller waits on this.
    return Promise.resolve(drawer.close());
  }

  return null;
}

/**
 * @param {MouseEvent} event
 */
function handleClick(event) {
  // Let the browser handle modified clicks - they open a new tab or window,
  // where scrolling this document would be wrong.
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const link = /** @type {HTMLElement | null} */ (event.target)?.closest?.('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return;

  // Only same-document links. `link.href` is absolute, so this compares the
  // resolved path rather than the authored one - `/#about` from the home page
  // and `#about` both match, while `/about#top` correctly does not.
  if (link.origin !== window.location.origin) return;
  if (link.pathname !== window.location.pathname) return;

  const target = resolveTarget(link.hash);
  if (!target) return;

  event.preventDefault();

  // Keeps the URL shareable and the back button meaningful, without the jump
  // that assigning `location.hash` would cause. Done before the scroll so the
  // address bar is correct even while the drawer is still animating shut.
  if (link.hash !== window.location.hash) {
    history.pushState(null, '', link.hash);
  }

  // A drawer normally closes because the link navigated away. An in-page anchor
  // does not navigate, so tapping a menu item would scroll the page behind a
  // drawer that stayed open over it.
  const closing = closeEnclosingDrawer(link);

  if (closing) {
    closing.then(() => {
      scrollToTarget(target, 'smooth');
      focusTarget(target);
    });
    return;
  }

  scrollToTarget(target, 'smooth');
  focusTarget(target);
}

/**
 * Handles a hash that is already in the URL on load - the case where a menu
 * link was followed from another page.
 *
 * @param {ScrollBehavior} behavior
 */
function scrollToCurrentHash(behavior) {
  const target = resolveTarget(window.location.hash);
  if (!target) return;

  scrollToTarget(target, behavior);
  focusTarget(target);
}

document.addEventListener('click', handleClick);

// popstate covers the back/forward buttons moving between hashes on this page.
window.addEventListener('popstate', () => scrollToCurrentHash('smooth'));

// On arrival the browser may already have attempted its own jump. Re-running it
// here corrects for the squeeze layout and applies the header offset. 'instant'
// because the page is still settling - a smooth scroll would animate from a
// position the user never saw.
if (window.location.hash) {
  // Deferred a frame so section stylesheets and the header height script have
  // applied; measuring before that gives a target position that shifts.
  requestAnimationFrame(() => scrollToCurrentHash('instant'));
}
